/**
 * @file removeAttachmentVersion.ts
 * @description Client wrapper for the `removeAttachmentVersion` callable
 * (SYD-2478): detaching one version of a file, or the whole file, from one
 * artifact.
 *
 * Consumers: `useAttachments.removeVersion` and `useAttachments.removeFile`,
 * driven by the attachments panel's delete actions. The callable is the sole
 * writer — it prunes both documents in one transaction — so neither caller
 * writes the attachments map itself, and neither reports the result through
 * `onChange`. A whole-map write built from the panel's render is the defect this
 * replaced: it erased versions a concurrent commit had landed. The panel
 * reflects both documents through their live subscriptions instead.
 *
 * `versionId: null` is the value for "the whole file", not an absence, and both
 * callers pass it explicitly. The callable refuses a request that omits the
 * field — reading an absence as "detach every version" would let any caller that
 * lost a key destroy the file and be told it succeeded.
 */

import { httpsCallable } from 'firebase/functions';
import type { RemoveAttachmentVersionRequest, RemoveAttachmentVersionResult } from '#types';
import { functions } from './firebaseFunctions';

/**
 * Removes one version of an attachment from an artifact, or every version of it
 * when `versionId` is null.
 *
 * @param request The file, the artifact to detach it from, and the version to
 *   remove — null for the whole file.
 * @returns Which version records were destroyed, where the artifact's pointer
 *   ended up, and whether the attachment document itself was deleted.
 */
export const removeAttachmentVersion = async (
    request: RemoveAttachmentVersionRequest,
): Promise<RemoveAttachmentVersionResult> => {
    const removeAttachmentVersionFn = httpsCallable<
        RemoveAttachmentVersionRequest,
        RemoveAttachmentVersionResult
    >(functions, 'removeAttachmentVersion');

    const response = await removeAttachmentVersionFn(request);

    return response.data;
};
